import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import auth from "../../../configs/auth";
import { getInvoiceTitleRequest } from "../../../redux/Configuration/configurationSlice";

const InvoicePage = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [invoiceLoading, setInvoiceLoading] = useState(true);
  const [invoiceError, setInvoiceError] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState({});
  const invoiceTitle = useSelector(
    (state) => state.appConfig.invoiceTitle.invoiceTitle,
  );
  const taxType = useSelector((state) => state.appConfig.invoiceTitle.taxtype);

  const queryParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    async function fetchData() {
      try {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/v1/transactions/getInvoice/${id}`;

        let headers = {
          "Content-Type": "application/json", // for JSON requests
        };

        // Call API using fetch with headers
        let response = await fetch(apiUrl, {
          method: "GET", // or "POST", "PUT", "DELETE"
          headers: headers,
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        let data = await response.json();
        setInvoiceLoading(false);
        setSelectedInvoice(data);

        console.log(data);
      } catch (err) {
        console.error(err);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (selectedInvoice?.transaction?.jeweller) {
      dispatch(
        getInvoiceTitleRequest({
          jeweller: selectedInvoice.transaction.jeweller,
        }),
      );
    }
  }, [selectedInvoice]);

  if (invoiceLoading) return <div>Loading...</div>;
  if (invoiceError) return <div>Error: {invoiceError}</div>;
  if (!selectedInvoice) return null;

  const txn = selectedInvoice.transaction ?? {};
  const effectiveTaxType = selectedInvoice?.taxtype || taxType || "GST";
  const metalAmount = Math.abs(
    Number(txn.final_amount ?? 0) - Number(txn.total_tax ?? 0),
  );
  const cgst = Number(txn.cgst ?? selectedInvoice?.cgst ?? 0);
  const sgst = Number(txn.sgst ?? selectedInvoice?.sgst ?? 0);
  const igst = Number(txn.igst ?? selectedInvoice?.igst ?? 0);
  const totalTax = Number(txn.total_tax ?? 0);
  const cess = Number(txn.cess ?? 0);
  const showSgst = sgst > 0;
  const showCgst = cgst > 0;
  const showIgst = igst > 0;
  const showVatTax = effectiveTaxType === "VAT" && totalTax > 0;
  const showVatCess = effectiveTaxType === "VAT" && cess > 0;
  const showTotalTaxOnly =
    effectiveTaxType === "GST" &&
    totalTax > 0 &&
    !showSgst &&
    !showCgst &&
    !showIgst;

  return (
    <Box sx={{ backgroundColor: "#f5f5f5", minHeight: "100vh", py: 8, px: 2 }}>
      <Paper
        elevation={3}
        sx={{
          maxWidth: "900px",
          margin: "0 auto",
          padding: 4,
          borderRadius: 4,
          backgroundColor: "#fff",
        }}
      >
        <Box textAlign="center" mb={selectedInvoice?.logo ? 4 : 0}>
          {selectedInvoice?.logo && (
            <Box
              component="img"
              src={selectedInvoice?.logo}
              alt="Jeweller Logo"
              sx={{
                maxHeight: 180,
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
            />
          )}
        </Box>
        {/* Invoice Title */}
        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="center"
          gutterBottom
          sx={{ mb: 4, textTransform: "uppercase", color: "#333" }}
        >
          {selectedInvoice?.transaction?.order?.metalName}{" "}
          {invoiceTitle?.[selectedInvoice?.transaction?.orderType] || "Invoice"}
        </Typography>

        {/* Customer & Jeweller Info */}
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Box>
            <Typography>
              <strong>Name:</strong>{" "}
              {selectedInvoice.transaction.customer?.firstName}{" "}
              {selectedInvoice.transaction.customer?.middleName}{" "}
              {selectedInvoice.transaction.customer?.lastName}
            </Typography>
            <Typography>
              <strong>Contact No:</strong>{" "}
              {selectedInvoice.transaction.customer?.phone}
            </Typography>
            {selectedInvoice.transaction.customeraddress && (
              <Typography>
                <strong>Address:</strong>{" "}
                {[
                  selectedInvoice.transaction.customeraddress?.address1,
                  selectedInvoice.transaction.customeraddress?.area,
                  selectedInvoice.transaction.customeraddress?.city,
                  selectedInvoice.transaction.customeraddress?.state,
                  selectedInvoice.transaction.customeraddress?.pincode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
            <Typography>
              <strong>Date:</strong>{" "}
              {new Date(
                selectedInvoice.transaction.order?.createdAt,
              ).toLocaleDateString()}
            </Typography>
            <Typography>
              <strong>Invoice No:</strong>{" "}
              {["sell", "redeem"].includes(
                selectedInvoice.transaction?.orderType?.toLowerCase(),
              )
                ? selectedInvoice.transaction?.order?.order_id
                : selectedInvoice.transaction?.order?.invoiceNumber ||
                  selectedInvoice.transaction?.order?.invoiceNumber}
            </Typography>
            <Typography>
              <strong>Payment Type:</strong>{" "}
              {selectedInvoice.transaction.order?.paymentType}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography>
              <strong>{selectedInvoice.appName}</strong>
            </Typography>
            {effectiveTaxType == "GST" && (
              <Typography>
                <strong>GST No:</strong>{" "}
                {selectedInvoice.transaction.jewelleraddress?.gst}
              </Typography>
            )}
            <Typography>
              <strong>Phone:</strong>{" "}
              {selectedInvoice.transaction.jewelleraddress?.phone}
            </Typography>
            {selectedInvoice.transaction.jewelleraddress?.email && (
              <Typography>
                <strong>Email:</strong>{" "}
                {selectedInvoice.transaction.jewelleraddress.email}
              </Typography>
            )}
            <Typography>
              <strong>Address:</strong>{" "}
              {selectedInvoice.transaction.jewelleraddress?.address1}
            </Typography>
            <Typography>
              <strong>CIN No:</strong>{" "}
              {selectedInvoice.transaction.jewelleraddress?.cin || "N.A"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Items Table */}
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
            <TableRow>
              <TableCell>
                <strong>Description</strong>
              </TableCell>
              <TableCell>
                <strong>HSN Code</strong>
              </TableCell>
              <TableCell>
                <strong>Purity</strong>
              </TableCell>
              <TableCell>
                <strong>Gross wt.</strong>
              </TableCell>
              <TableCell>
                <strong>Net wt.</strong>
              </TableCell>
              <TableCell>
                <strong>Rate Per Gram</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell>
                <strong>Amount</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                {selectedInvoice?.transaction?.orderType === "Giftcancelled"
                  ? "Gift - Cancelled"
                  : selectedInvoice?.transaction?.orderType}
              </TableCell>
              <TableCell>
                {selectedInvoice.transaction?.metalName !== "Money" &&
                selectedInvoice.transaction?.metalName !== "Product"
                  ? selectedInvoice.transaction?.bullion?.hsnCode || [
                      selectedInvoice.transaction?.metalName,
                    ] ||
                    0
                  : 0}
              </TableCell>

              <TableCell>
                {selectedInvoice.transaction?.metalName !== "Money" &&
                selectedInvoice.transaction?.metalName !== "Product"
                  ? `${
                      selectedInvoice.transaction?.bullion?.karat
                        ? selectedInvoice.transaction?.bullion?.karat + "k - "
                        : ""
                    }${selectedInvoice.transaction?.bullion?.purity || ""}`
                  : ""}
              </TableCell>
              <TableCell>{selectedInvoice.transaction?.weight}</TableCell>
              <TableCell>{selectedInvoice.transaction?.weight}</TableCell>
              <TableCell>{selectedInvoice.transaction?.rate}</TableCell>
              <TableCell>{selectedInvoice.transaction?.status}</TableCell>
              <TableCell>
                ₹
                {selectedInvoice.transaction?.orderType === "Redeem" ||
                selectedInvoice.transaction?.orderType === "GiftReceived"
                  ? "0.00"
                  : Math.abs(
                      selectedInvoice.transaction?.final_amount -
                        selectedInvoice.transaction?.total_tax,
                    )?.toFixed(2)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Summary */}
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Table size="small" sx={{ maxWidth: 320 }}>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Amount</strong>
                </TableCell>
                <TableCell>
                  ₹
                  {txn.orderType === "GiftReceived"
                    ? "0.00"
                    : metalAmount.toFixed(2)}
                </TableCell>
              </TableRow>
              {showSgst && (
                <TableRow>
                  <TableCell>
                    <strong>SGST</strong>
                  </TableCell>
                  <TableCell>₹{sgst.toFixed(2)}</TableCell>
                </TableRow>
              )}
              {showCgst && (
                <TableRow>
                  <TableCell>
                    <strong>CGST</strong>
                  </TableCell>
                  <TableCell>₹{cgst.toFixed(2)}</TableCell>
                </TableRow>
              )}
              {showIgst && (
                <TableRow>
                  <TableCell>
                    <strong>IGST</strong>
                  </TableCell>
                  <TableCell>₹{igst.toFixed(2)}</TableCell>
                </TableRow>
              )}
              {showTotalTaxOnly && (
                <TableRow>
                  <TableCell>
                    <strong>Total GST</strong>
                  </TableCell>
                  <TableCell>₹{totalTax.toFixed(2)}</TableCell>
                </TableRow>
              )}
              {showVatTax && (
                <TableRow>
                  <TableCell>
                    <strong>Tax</strong>
                  </TableCell>
                  <TableCell>₹{(totalTax - cess).toFixed(2)}</TableCell>
                </TableRow>
              )}
              {showVatCess && (
                <TableRow>
                  <TableCell>
                    <strong>Cess</strong>
                  </TableCell>
                  <TableCell>₹{cess.toFixed(2)}</TableCell>
                </TableRow>
              )}
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                <TableCell>
                  <strong>Net Payable</strong>
                </TableCell>
                <TableCell>
                  <strong>
                    ₹
                    {selectedInvoice.transaction?.orderType === "Redeem"
                      ? (
                          selectedInvoice.transaction?.final_amount || 0
                        ).toFixed(2)
                      : (
                          selectedInvoice.transaction?.final_amount || 0
                        ).toFixed(2)}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Footer */}
        <Typography
          variant="body2"
          mt={4}
          textAlign="center"
          color="text.secondary"
        >
          # This is a computer-generated Invoice. No signature is required.
          <br /># Your Metal is kept safe with {selectedInvoice.appName}.
          <br /># It can be collected from the Jeweller's Store as per Terms &
          Conditions.
        </Typography>
      </Paper>
    </Box>
  );
};

export default InvoicePage;
